import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Gateway, GatewayState } from 'types/gateway';
import { RequireAtLeastOne } from 'types/utility_types';

import { useAPI } from 'hooks/api/useAPI';
import { updateQueriesData } from 'services/update_queries_data';

type UseUpdateGatewayParams = { id: string } & RequireAtLeastOne<{
  status?: GatewayState;
  name?: string;
  model?: string;
  ip_local?: string;
  ip_ext?: string;
  mac_address?: string;
  version?: string;
  max_running_deployments?: number;
  max_deployments_per_process?: number;
  onvif_request_timeout_secs?: number;
  configuration_id?: string;
}>;

export function useUpdateGateway({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<Gateway, APIError, UseUpdateGatewayParams> = {}) {
  const { applicationID, client } = useAPI();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, ...params }) => {
      const { data } = await client.put<Gateway>(
        `/apps/${applicationID}/gateways/${id}`,
        params
      );

      return data;
    },
    {
      ...options,
      async onMutate(params) {
        await queryClient.cancelQueries(['gateway']);
        await queryClient.cancelQueries(['gateways']);

        onMutate?.(params);

        const { id, ...rest } = params;

        return updateQueriesData<Gateway>({
          queryClient,
          listQueryKey: ['gateways'],
          singleQueryKey: ['gateway'],
          ids: [id],
          updateData: (gateway) => ({ ...gateway, ...rest }),
        });
      },
      onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(...args) {
        onSettled?.(...args);
        queryClient.invalidateQueries(['gateway']);
        queryClient.invalidateQueries(['gateways']);
      },
    }
  );
}
